/**
 * The faces Horton can wear, and nothing that knows how to draw one.
 *
 * Kept apart from `embed.ts` so the chat can name a face and type a state
 * without pulling the mixer and the authorities into its own bundle. Those
 * load with the renderer, on demand, the first time a face is actually shown.
 */
import type { ConversationState } from '@horton/presence-contract'

/** What the conversation is doing, as far as a face needs to know. */
export type FaceState = Exclude<ConversationState, 'interrupted'>

export interface FaceSpec {
  id: string
  label: string
  /**
   * `mesh`: a glTF carrying ARKit morph targets, drawn by Three.js — a whole
   * person, with a body that can hold a pose. `drawn`: a portrait plus one
   * drawing per expression, composited by Pixi. See VENDORED.md for both.
   */
  kind: 'mesh' | 'drawn'
  /** Where the package lives, for mesh faces. Drawn faces are found by id. */
  url?: string
}

export const FACES: readonly FaceSpec[] = [
  // Bought from ThreeDee (horton-videos/docs/threedee-license-email.md).
  // 52 of 52 ARKit shapes bound under their own names, real eyeballs, and a
  // library of body poses. This is the meshopt export at 11 MB; the plain
  // GLB in the purchase is 201 MB for the same character.
  { id: 'business-woman', label: 'business woman', kind: 'mesh',
    url: '/avatars/threedee/business-woman.glb' },
  { id: 'flat-woman', label: 'flat vector woman', kind: 'drawn' },
]

export const DEFAULT_FACE = 'business-woman'

export function faceSpec(id?: string | null): FaceSpec {
  return FACES.find((f) => f.id === id) ?? FACES.find((f) => f.id === DEFAULT_FACE)!
}
